import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Button,
  Grid,
  Avatar,
  Tag,
  Alert,
  Spin,
  Divider,
  Tooltip,
  theme,
  Modal,
  Table,
  DatePicker,
  Tabs,
  Statistic,
  Progress,
  message,
  Skeleton,
  Empty,
  List,
  Input
} from 'antd';
import {
  UserOutlined,
  HomeOutlined,
  CalendarOutlined,
  TeamOutlined,
  AppstoreOutlined,
  BellOutlined,
  LogoutOutlined,
  DashboardOutlined,
  SettingOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  EditOutlined,
  ReloadOutlined,
  BarChartOutlined,
  CoffeeOutlined,
  RestOutlined,
  RiseOutlined,
  FallOutlined,ShopOutlined ,
  HotelOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { BASE_URL } from '../utils/constants';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

const Dashboard = () => {
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(true);
  const [hotel, setHotel] = useState('');
  const [hotelCodigo, setHotelCodigo] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  
  // Estados para ocupación y estadísticas
  const [stats, setStats] = useState({
    ocupacion: 0,
    habitacionesOcupadas: 0,
    totalHabitaciones: 0,
    huespedes: 0,
    entradasHoy: 0,
    salidasHoy: 0,
    entradasPax: 0,
    salidasPax: 0
  });
  
  // Estados para listados de reservas
  const [reservas, setReservas] = useState([]);
  const [filterType, setFilterType] = useState('checkin');
  const [loadingReservas, setLoadingReservas] = useState(false);
  const [agrupadoPorReserva, setAgrupadoPorReserva] = useState(true);
  const [soloGrupos, setSoloGrupos] = useState(false);
  
  // Estados para servicios de cocina
  const [serviciosCocina, setServiciosCocina] = useState({
    desayunos: 0,
    almuerzos: 0,
    cenas: 0,
    picnicD: 0,
    picnicA: 0,
    picnicC: 0
  });
  
  // Estados para agenda general
  const [agendaGeneral, setAgendaGeneral] = useState('');
  const [loadingAgenda, setLoadingAgenda] = useState(false);
  const [editandoAgenda, setEditandoAgenda] = useState(false);
  
  // Estados para hoteles
  const [hoteles, setHoteles] = useState([]);
  const [modalHotelesVisible, setModalHotelesVisible] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [loadingHoteles, setLoadingHoteles] = useState(false);
  
  // Estados para grid de habitaciones
  const [habitacionesData, setHabitacionesData] = useState([]);
  const [loadingHabitaciones, setLoadingHabitaciones] = useState(false);
  
  const navigate = useNavigate();
  const breakpoints = useBreakpoint();
  const { token } = theme.useToken();
  
  const isMobile = !breakpoints.md;
  const isDesktop = breakpoints.lg;

  // Obtener perfil de usuario
  const fetchUserProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/usuarios/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUserData(response.data.user);
      }
    } catch (error) {
      console.error('Error obteniendo perfil:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    }
  }, [navigate]);

  // Obtener lista de hoteles
  const fetchHoteles = useCallback(async () => {
    try {
      setLoadingHoteles(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/hoteles/listaHoteles`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setHoteles(response.data.hoteles);
        
        // Verificar si hay hotel guardado en localStorage
        const savedHotel = localStorage.getItem('hotelSeleccionado');
        if (savedHotel) {
          const parsed = JSON.parse(savedHotel);
          const hotelExistente = response.data.hoteles.find(h => h.codigo === parsed.codigo);
          if (hotelExistente) {
            setHotel(hotelExistente.nombre);
            setHotelCodigo(hotelExistente.codigo);
            setSelectedHotel(hotelExistente.codigo);
            return hotelExistente.codigo;
          }
        }
        
        // Si no hay hotel guardado, usar el primero
        if (response.data.hoteles.length > 0) {
          const primerHotel = response.data.hoteles[0];
          setHotel(primerHotel.nombre);
          setHotelCodigo(primerHotel.codigo);
          setSelectedHotel(primerHotel.codigo);
          return primerHotel.codigo;
        }
      }
      return null;
    } catch (error) {
      console.error('Error obteniendo lista de hoteles:', error);
      message.error('Error al cargar los hoteles');
      return null;
    } finally {
      setLoadingHoteles(false);
    }
  }, []);

  // Función para obtener estadísticas de ocupación
  const fetchOcupacion = useCallback(async (codigoHotel, fecha) => {
    if (!codigoHotel) return;
    
    try {
      const token = localStorage.getItem('token');
      const fechaFormateada = fecha.format('YYYY-MM-DD');
      
      const response = await axios.get(
        `${BASE_URL}/api/dashboard/ocupacion`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { codigoHotel, fecha: fechaFormateada }
        }
      );
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error obteniendo ocupación:', error);
    }
  }, []);

  // Función para obtener reservas filtradas
  const fetchReservas = useCallback(async (codigoHotel, fecha) => {
    if (!codigoHotel) return;
    
    setLoadingReservas(true);
    try {
      const token = localStorage.getItem('token');
      const fechaFormateada = fecha.format('YYYY-MM-DD');
      
      const response = await axios.post(
        `${BASE_URL}/api/dashboard/reservas`,
        {
          codigoHotel,
          fecha: fechaFormateada,
          filterType,
          agrupadoPorReserva,
          soloGrupos
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {

          
          

        setReservas(response.data.data);
      }
    } catch (error) {
      console.error('Error obteniendo reservas:', error);
    } finally {
      setLoadingReservas(false);
    }
  }, [filterType, agrupadoPorReserva, soloGrupos]);

  // Función para obtener servicios de cocina
  const fetchServiciosCocina = useCallback(async (codigoHotel, fecha) => {
    if (!codigoHotel) return;
    
    try {
      const token = localStorage.getItem('token');
      const fechaFormateada = fecha.format('YYYY-MM-DD');
      
      const response = await axios.get(
        `${BASE_URL}/api/dashboard/servicios-cocina`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { codigoHotel, fecha: fechaFormateada }
        }
      );
      
      if (response.data.success) {
        setServiciosCocina(response.data.data);
      }
    } catch (error) {
      console.error('Error obteniendo servicios de cocina:', error);
    }
  }, []);

  // Función para obtener agenda general
  const fetchAgendaGeneral = useCallback(async (codigoHotel, fecha) => {
    if (!codigoHotel) return;
    
    setLoadingAgenda(true);
    try {
      const token = localStorage.getItem('token');
      const fechaFormateada = fecha.format('YYYY-MM-DD');
      
      const response = await axios.get(
        `${BASE_URL}/api/dashboard/agenda-general`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { codigoHotel, fecha: fechaFormateada }
        }
      );
      
      if (response.data.success) {
        setAgendaGeneral(response.data.texto || '');
      }
    } catch (error) {
      console.error('Error obteniendo agenda general:', error);
    } finally {
      setLoadingAgenda(false);
    }
  }, []);

  // Función para obtener distribución de habitaciones
  const fetchHabitaciones = useCallback(async (codigoHotel, fecha) => {
    if (!codigoHotel) return;
    
    setLoadingHabitaciones(true);
    try {
      const token = localStorage.getItem('token');
      const fechaFormateada = fecha.format('YYYY-MM-DD');
      
      const response = await axios.get(
        `${BASE_URL}/api/dashboard/habitaciones`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { codigoHotel, fecha: fechaFormateada }
        }
      );
      
      if (response.data.success) {
        setHabitacionesData(response.data.data);
      }
    } catch (error) {
      console.error('Error obteniendo distribución de habitaciones:', error);
    } finally {
      setLoadingHabitaciones(false);
    }
  }, []);

  // Función para actualizar último hotel seleccionado
  const updateUltimoHotel = async (codigoHotel) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BASE_URL}/api/hoteles/ultimoHotel`,
        { codigoHotel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error actualizando último hotel:', error);
    }
  };

  // Función para cambiar de hotel
  const handleSelectHotel = async (codigoHotel) => {
    try {
      const hotelSeleccionado = hoteles.find(h => h.codigo === codigoHotel);
      if (hotelSeleccionado) {
        setHotel(hotelSeleccionado.nombre);
        setHotelCodigo(codigoHotel);
        setSelectedHotel(codigoHotel);
        
        await updateUltimoHotel(codigoHotel);
        
        localStorage.setItem('hotelSeleccionado', JSON.stringify({
          codigo: codigoHotel,
          nombre: hotelSeleccionado.nombre,
          terminal: hotelSeleccionado.terminal
        }));
        
        setModalHotelesVisible(false);
        
        message.success(`Hotel cambiado a: ${hotelSeleccionado.nombre}`);
        
        // Recargar todos los datos con el nuevo hotel
        cargarTodosLosDatos(codigoHotel, selectedDate);
      }
    } catch (error) {
      console.error('Error al cambiar de hotel:', error);
      message.error('No se pudo cambiar el hotel seleccionado');
    }
  };

  // Función para cargar todos los datos
  const cargarTodosLosDatos = useCallback(async (codigoHotel, fecha) => {
    if (!codigoHotel) return;
    
    await Promise.all([
      fetchOcupacion(codigoHotel, fecha),
      fetchReservas(codigoHotel, fecha),
      fetchServiciosCocina(codigoHotel, fecha),
      fetchAgendaGeneral(codigoHotel, fecha),
      fetchHabitaciones(codigoHotel, fecha)
    ]);
  }, [fetchOcupacion, fetchReservas, fetchServiciosCocina, fetchAgendaGeneral, fetchHabitaciones]);

  // Función para refrescar todos los datos
  const refreshAllData = useCallback(() => {
    if (hotelCodigo) {
      cargarTodosLosDatos(hotelCodigo, selectedDate);
    }
  }, [hotelCodigo, selectedDate, cargarTodosLosDatos]);

  // Función para actualizar agenda general
  const updateAgendaGeneral = async (texto) => {
    if (!hotelCodigo) return;
    
    try {
      const token = localStorage.getItem('token');
      const fechaFormateada = selectedDate.format('YYYY-MM-DD');
      
      const response = await axios.post(
        `${BASE_URL}/api/dashboard/agenda-general`,
        {
          codigoHotel: hotelCodigo,
          fecha: fechaFormateada,
          texto: texto
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setAgendaGeneral(texto);
        setEditandoAgenda(false);
        message.success('Agenda guardada correctamente');
      }
    } catch (error) {
      console.error('Error guardando agenda general:', error);
      message.error('Error al guardar la agenda');
    }
  };

  // Función para hacer check-in/check-out
  const handleCheckInOut = async (reserva, tipo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/dashboard/check-in-out`,
        {
          numReserva: reserva.numReserva,
          linea: reserva.linea,
          tipo: tipo,
          fecha: selectedDate.format('YYYY-MM-DD')
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        message.success(`${tipo === 'IN' ? 'Check-in' : 'Check-out'} realizado correctamente`);
        refreshAllData();
      }
    } catch (error) {
      console.error('Error realizando check-in/out:', error);
      message.error('Error al procesar la operación');
    }
  };

  // Función para ver detalles de reserva
  const handleVerDetalles = async (reserva) => {
    try {
      const token = localStorage.getItem('token');
      console.log(reserva.numReserva,reserva.linea,reserva.numOcupante);
      console.log(reserva.NumReserva,reserva.Linea,reserva.NumOcupante);
      
      const response = await axios.get(
        `${BASE_URL}/api/dashboard/reserva-detalle`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            numReserva: reserva.NumReserva,
            linea: reserva.Linea,
            numOcupante: 1
          }
        }
      );
    
      if (response.data.success) {
        Modal.info({
          title: `Detalles de Reserva ${reserva.numReserva}`,
          width: 500,
          content: (
            <div>
              <p><strong>Nombre:</strong> {response.data.Nombre}</p>
              <p><strong>Habitación:</strong> {response.data.NumHab || 'No asignada'}</p>
              <p><strong>Entrada:</strong> {response.data.Entrada}</p>
              <p><strong>Salida:</strong> {response.data.Salida}</p>
              <p><strong>Adultos:</strong> {response.data.Adultos}</p>
              <p><strong>Niños:</strong> {response.data.Ninos}</p>
              <p><strong>Agencia:</strong> {response.data.Agencia}</p>
              <p><strong>Teléfono:</strong> {response.data.Telefono}</p>
              <p><strong>Email:</strong> {response.data.Email}</p>
            </div>
          )
        });
      }
    } catch (error) {
      console.error('Error obteniendo detalles:', error);
      message.error('Error al obtener detalles de la reserva');
    }
  };

  // Carga inicial
  useEffect(() => {
    const inicializar = async () => {
      setLoading(true);
      
      // Obtener perfil de usuario
      await fetchUserProfile();
      
      // Obtener hoteles y el primer hotel
      const codigoHotelInicial = await fetchHoteles();
      
      if (codigoHotelInicial) {
        // Cargar todos los datos
        await cargarTodosLosDatos(codigoHotelInicial, selectedDate);
      }
      
      setLoading(false);
    };
    
    inicializar();
  }, [fetchUserProfile, fetchHoteles, cargarTodosLosDatos, selectedDate]);

  // Efecto para cuando cambia la fecha o los filtros
  useEffect(() => {
    if (hotelCodigo && !loading) {
      cargarTodosLosDatos(hotelCodigo, selectedDate);
    }
  }, [selectedDate, filterType, agrupadoPorReserva, soloGrupos, hotelCodigo, loading, cargarTodosLosDatos]);

  // Columnas para la tabla de reservas
  const reservasColumns = [
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 80,
      render: (NumHab) => NumHab || '—',
      sorter: (a, b) => (a.NumHab || 0) - (b.NumHab || 0)
    },
    {
      title: 'Nombre',
      dataIndex: 'Nombre',
      key: 'Nombre',
      width: 200,
      render: (Nombre, record) => (
        <Tooltip title="Click para ver detalles">
          <Button 
            type="link" 
            onClick={() => handleVerDetalles(record)}
            style={{ padding: 0 }}
          >
            {Nombre}
          </Button>
        </Tooltip>
      )
    },
    {
      title: 'Reserva',
      dataIndex: 'NumReserva',
      key: 'NumReserva',
      width: 100
    },
    {
      title: 'Adultos',
      dataIndex: 'Adultos',
      key: 'Adultos',
      width: 70,
      align: 'center'
    },
    {
      title: 'Niños',
      dataIndex: 'Ninos',
      key: 'Ninos',
      width: 70,
      align: 'center'
    },
    {
      title: 'Tipo Hab',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 80
    },
    {
      title: 'Estado',
      dataIndex: 'Estado',
      key: 'Estado',
      width: 100,
      render: (Estado) => {
        const estadoConfig = {
          'Entrada': { color: 'processing', icon: <RiseOutlined /> },
          'Ocupada': { color: 'success', icon: <CheckCircleOutlined /> },
          'Salida': { color: 'warning', icon: <FallOutlined /> }
        };
        const config = estadoConfig[Estado] || { color: 'default', icon: null };
        return (
          <Tag color={config.color} icon={config.icon}>
            {Estado}
          </Tag>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          {record.CheckIn === 'Check-In' && (
            <Tooltip title="Realizar Check-in">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleCheckInOut(record, 'IN')}
                disabled={!record.NumHab}
              />
            </Tooltip>
          )}
          {record.CheckOut === 'Check-Out' && (
            <Tooltip title="Realizar Check-out">
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => handleCheckInOut(record, 'OUT')}
              />
            </Tooltip>
          )}
          <Tooltip title="Ver Detalles">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleVerDetalles(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Columnas para la tabla de habitaciones
  const habitacionesColumns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 100,
      render: (fecha) => dayjs(fecha).format('DD/MM')
    },
    {
      title: 'Total Habs',
      dataIndex: 'tot',
      key: 'tot',
      width: 80,
      align: 'center'
    },
    {
      title: 'Ocupadas',
      dataIndex: 'totalocupadas',
      key: 'totalocupadas',
      width: 80,
      align: 'center',
      render: (val) => <span style={{ color: token.colorSuccess }}>{val}</span>
    },
    {
      title: 'Bloqueadas',
      dataIndex: 'bloq',
      key: 'bloq',
      width: 80,
      align: 'center',
      render: (val) => <span style={{ color: token.colorWarning }}>{val}</span>
    },
    {
      title: 'Ficticias',
      dataIndex: 'fict',
      key: 'fict',
      width: 80,
      align: 'center'
    },
    {
      title: 'Libres',
      dataIndex: 'totallibres',
      key: 'totallibres',
      width: 80,
      align: 'center',
      render: (val) => <span style={{ color: token.colorPrimary, fontWeight: 'bold' }}>{val}</span>
    }
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" tip="Cargando dashboard..." />
        <Text type="secondary" style={{ marginTop: 20 }}>
          Preparando tu información...
        </Text>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: isMobile ? '12px' : '24px',
      maxWidth: '100%',
      overflowX: 'hidden',
      backgroundColor: token.colorBgLayout,
      minHeight: '100vh'
    }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Card 
          bordered={false}
          style={{ 
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`
          }}
          bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
        >
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col flex="auto">
              <Space direction="vertical" size="small">
                <Title level={isMobile ? 3 : 2} style={{ margin: 0, color: 'white', fontWeight: 600 }}>
                  Dashboard 
                </Title>
                <Text style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  Bienvenido, {userData.UsuarioCorto || userData.usuario || 'Usuario'}
                </Text>
                <Space>
                  <CalendarOutlined style={{ color: 'white' }} />
                  <Text style={{ color: 'white' }}>
                    {selectedDate.format('dddd, DD [de] MMMM [de] YYYY')}
                  </Text>
                </Space>
              </Space>
            </Col>
            <Col>
              <Space wrap>
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => date && setSelectedDate(date)}
                  format="DD/MM/YYYY"
                  style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                />
                <Button 
                  type="default"
                  icon={<BellOutlined />}
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white'
                  }}
                />
                <Button 
                  danger
                  icon={<LogoutOutlined />}
                  onClick={() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/login');
                  }}
                />
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Estadísticas */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} hoverable onClick={() => setModalHotelesVisible(true)}>
              <Statistic
                title="Hotel Actual"
                value={hotel || 'Seleccionar Hotel'}
                prefix={<ShopOutlined />}
                valueStyle={{ fontSize: isMobile ? 14 : 16, color: hotel ? token.colorText : token.colorPrimary }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Click para cambiar
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false}>
              <Statistic
                title="Ocupación"
                value={stats.ocupacion}
                suffix="%"
                prefix={<BarChartOutlined />}
                valueStyle={{ color: token.colorSuccess }}
              />
              <Progress percent={stats.ocupacion} size="small" showInfo={false} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {stats.habitacionesOcupadas} de {stats.totalHabitaciones} habs
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false}>
              <Statistic
                title="Huéspedes"
                value={stats.huespedes}
                prefix={<TeamOutlined />}
              />
              <Text type="secondary">
                {stats.huespedes} personas
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Statistic
                  title="Entradas"
                  value={stats.entradasHoy}
                  prefix={<RiseOutlined />}
                  valueStyle={{ color: token.colorSuccess }}
                />
                <Divider type="vertical" />
                <Statistic
                  title="Salidas"
                  value={stats.salidasHoy}
                  prefix={<FallOutlined />}
                  valueStyle={{ color: token.colorWarning }}
                />
              </div>
              <Text type="secondary">
                {stats.entradasPax} / {stats.salidasPax} pax
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Servicios de Cocina */}
        <Card title="Servicios de Cocina" bordered={false}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Avatar size={48} icon={<CoffeeOutlined />} style={{ backgroundColor: token.colorPrimary }} />
                <Title level={4} style={{ margin: '8px 0 0' }}>{serviciosCocina.desayunos}</Title>
                <Text type="secondary">Desayunos</Text>
                {serviciosCocina.picnicD > 0 && (
                  <Tag color="blue" style={{ marginTop: 4, display: 'block' }}>Picnic: {serviciosCocina.picnicD}</Tag>
                )}
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Avatar size={48} icon={<RestOutlined />} style={{ backgroundColor: token.colorWarning }} />
                <Title level={4} style={{ margin: '8px 0 0' }}>{serviciosCocina.almuerzos}</Title>
                <Text type="secondary">Almuerzos</Text>
                {serviciosCocina.picnicA > 0 && (
                  <Tag color="blue" style={{ marginTop: 4, display: 'block' }}>Picnic: {serviciosCocina.picnicA}</Tag>
                )}
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Avatar size={48} icon={<RestOutlined />} style={{ backgroundColor: token.colorError }} />
                <Title level={4} style={{ margin: '8px 0 0' }}>{serviciosCocina.cenas}</Title>
                <Text type="secondary">Cenas</Text>
                {serviciosCocina.picnicC > 0 && (
                  <Tag color="blue" style={{ marginTop: 4, display: 'block' }}>Picnic: {serviciosCocina.picnicC}</Tag>
                )}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Filtros para reservas */}
        <Card bordered={false}>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              type={filterType === 'checkin' ? 'primary' : 'default'}
              onClick={() => setFilterType('checkin')}
              icon={<CheckOutlined />}
            >
              Check-In
            </Button>
            <Button
              type={filterType === 'checkout' ? 'primary' : 'default'}
              onClick={() => setFilterType('checkout')}
              icon={<CloseOutlined />}
            >
              Check-Out
            </Button>
            <Button
              type={filterType === 'inhouse' ? 'primary' : 'default'}
              onClick={() => setFilterType('inhouse')}
              icon={<HomeOutlined />}
            >
              In House
            </Button>
            <Button
              type={filterType === 'todos' ? 'primary' : 'default'}
              onClick={() => setFilterType('todos')}
            >
              Todos
            </Button>
            <Divider type="vertical" />
            <Button
              type={agrupadoPorReserva ? 'primary' : 'default'}
              onClick={() => setAgrupadoPorReserva(!agrupadoPorReserva)}
              icon={<AppstoreOutlined />}
            >
              Agrupar por Reserva
            </Button>
            <Button
              type={soloGrupos ? 'primary' : 'default'}
              onClick={() => setSoloGrupos(!soloGrupos)}
              icon={<TeamOutlined />}
            >
              Solo Grupos
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={refreshAllData}
            >
              Refrescar
            </Button>
          </Space>

          {/* Tabla de reservas */}
          <Table
            columns={reservasColumns}
            dataSource={reservas}
            rowKey={(record) => `${record.numReserva}_${record.linea}_${record.numOcupante || 1}`}
            loading={loadingReservas}
            size={isMobile ? 'small' : 'middle'}
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
            }}
            locale={{ emptyText: <Empty description="No hay reservas para mostrar" /> }}
          />
        </Card>

        {/* Distribución de Habitaciones y Agenda */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Distribución de Habitaciones" bordered={false}>
              <Table
                columns={habitacionesColumns}
                dataSource={habitacionesData}
                rowKey="fecha"
                loading={loadingHabitaciones}
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={false}
              />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card 
              title="Agenda General" 
              bordered={false}
              extra={
                !editandoAgenda && (
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => setEditandoAgenda(true)}
                  >
                    Editar
                  </Button>
                )
              }
            >
              {loadingAgenda ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : editandoAgenda ? (
                <>
                  <TextArea
                    value={agendaGeneral}
                    onChange={(e) => setAgendaGeneral(e.target.value)}
                    rows={8}
                    placeholder="Escribe aquí las notas del día..."
                  />
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <Space>
                      <Button onClick={() => setEditandoAgenda(false)}>Cancelar</Button>
                      <Button type="primary" onClick={() => updateAgendaGeneral(agendaGeneral)}>
                        Guardar
                      </Button>
                    </Space>
                  </div>
                </>
              ) : (
                <Paragraph
                  style={{
                    backgroundColor: token.colorFillAlter,
                    padding: 16,
                    borderRadius: 8,
                    minHeight: 200,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {agendaGeneral || 'Sin notas para este día'}
                </Paragraph>
              )}
            </Card>
          </Col>
        </Row>

        {/* Footer */}
        {isDesktop && (
          <Card bordered={false} style={{ textAlign: 'center' }} bodyStyle={{ padding: '12px' }}>
            <Text type="secondary">
              Anphitrion v2.0.0 - Sistema de Gestión Hotelera
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              © {new Date().getFullYear()} - Todos los derechos reservados
            </Text>
          </Card>
        )}
      </Space>

      {/* Modal de selección de hotel */}
      <Modal
        title="Seleccionar Hotel"
        open={modalHotelesVisible}
        onCancel={() => setModalHotelesVisible(false)}
        footer={null}
        width={isMobile ? '90%' : 400}
        centered
      >
        {loadingHoteles ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" tip="Cargando hoteles..." />
          </div>
        ) : hoteles.length === 0 ? (
          <Empty description="No hay hoteles disponibles" />
        ) : (
          <List
            dataSource={hoteles}
            renderItem={(item) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  borderRadius: 8,
                  marginBottom: 8,
                  padding: '12px',
                  backgroundColor: selectedHotel === item.codigo ? `${token.colorPrimary}10` : 'transparent',
                  border: selectedHotel === item.codigo ? `1px solid ${token.colorPrimary}` : '1px solid #f0f0f0'
                }}
                onClick={() => handleSelectHotel(item.codigo)}
              >
                <List.Item.Meta
                  avatar={<Avatar icon={<ShopOutlined />} style={{ backgroundColor: token.colorPrimary }} />}
                  title={<Text strong>{item.nombre}</Text>}
                  description={`Código: ${item.codigo}${item.terminal ? ` - Terminal: ${item.terminal}` : ''}`}
                />
                {selectedHotel === item.codigo && <CheckOutlined style={{ color: token.colorPrimary }} />}
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;